import React, {useState, useMemo} from "react";
import {useTranslation} from "react-i18next";
import debounce from "lodash.debounce";
// local
import {Input} from "components/Input";
import {ModalRecipientMembersList} from "pages/OurRequests/components/RecipientsEdit/ModalRecipientMembersList";
import {Modal} from "components/Modal";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {Info} from "components/Info";
import {AddableFormikField} from "components/AddableFormikField";
import {IconMailLetter, IconHandOnRight} from "components/icons/index";
import {getFullName} from "helpers/helper";
import SelectionRow from "components/selects/SelectionRow";
import {color} from "constants/color.consts";

export const ModalRecipientMembers = ({
  organization,
  onClose,
  currentUser,
  currentOrganizationMembers,
  onAddEmailRecipients,
  onChoiceNotificationMember,
  emailLoading,
}) => {
  const {t} = useTranslation();

  const [inputValue, setInputValue] = useState("");

  const [membersToReceive, setMembersToReceive] = useState(organization.MembersToReceive);
  const isLastMember = useMemo(() => membersToReceive?.length < 2, [membersToReceive]);

  const membersToReceiveIds = useMemo(
    () => membersToReceive?.map((member) => member.id) ?? [],
    [membersToReceive]
  );
  const members = useMemo(
    () =>
      organization.members?.map((member) => ({
        ...member,
        isSelected: membersToReceiveIds.includes(member.id),
        OurOrganizationMembers: member.OurOrganizationMemberIds?.length
          ? member.OurOrganizationMemberIds.sort((a) =>
              a === currentUser.OrganizationMemberId ? -1 : 1
            )
              .map((id) => currentOrganizationMembers.find((member) => member.id === id))
              .filter(Boolean)
          : [],
      })),
    [
      membersToReceiveIds,
      organization,
      currentOrganizationMembers,
      currentUser.OrganizationMemberId,
    ]
  );
  const filteredMembers = useMemo(
    () =>
      inputValue
        ? members.filter(
            (member) =>
              member.firstName.toLowerCase().includes(inputValue) ||
              member.lastName.toLowerCase().includes(inputValue) ||
              member.email.toLowerCase().includes(inputValue)
          )
        : members,
    [members, inputValue]
  );
  const selectedMembers = useMemo(
    () => members?.filter((member) => member.isSelected) ?? [],
    [members]
  );

  const handleRemoveMember = (itemId) => {
    !isLastMember &&
      setMembersToReceive((prevState) =>
        prevState.filter((prevMember) => prevMember.id !== itemId)
      );
  };

  const Header = () => (
    <span className="__bold fz-12 __uppercase ml-4 mr-2">
      {t("RECIPIENT_MEMBERS_MODAL_TITLE")}
    </span>
  );

  const ActionButtons = () => (
    <div className="df-row-center mla">
      <ButtonTransparent
        name="RECIPIENT_MEMBERS_MODAL_CANCEL"
        color="red"
        small
        onClick={onClose}
        addClass="mr-4"
      />
      <ButtonTransparent
        name="RECIPIENT_MEMBERS_MODAL_OK"
        small
        nonTransparent
        onClick={() => {
          onChoiceNotificationMember(membersToReceive);
          onClose();
        }}
      />
    </div>
  );

  const searchOnChange = debounce(
    (e) => setInputValue(e.target.value.toLowerCase()),
    400
  );

  return (
    <Modal
      customTitle={<Header />}
      suffixTitleIcon={<Info tooltip="RECIPIENT_MEMBERS_MODAL_TITLE_TOOLTIP" />}
      width="800"
      percentageWide="50%"
      whiteTitle
      tightTitle
      rounded
      placeBitLow
      noCloseButton
      noCloseOnEsc
      onClose={onClose}
      headerCloseContent={<ActionButtons />}
    >
      <div className="recipients-tab__members-modal__content">
        {!!selectedMembers.length && (
          <div className="df ml-4">
            <IconHandOnRight addClass="mr-2 mt-2" color={color.green} />
            <SelectionRow
              values={selectedMembers.map((item) => ({
                value: item.id,
                label: getFullName(item.firstName, item.lastName),
                color: "green",
              }))}
              onRemove={handleRemoveMember}
              disabledRemoveButton={selectedMembers.length < 2}
              nonStretch
              addClassItem="mb-2"
            />
          </div>
        )}
        <div className="recipients-tab__members-modal__content__search-row">
          <span className="__uppercase __bold mr-2">
            {t("RECIPIENT_MEMBERS_MODAL_SEARCH_TITLE").format(organization.name)}
          </span>
          <Info tooltip="RECIPIENT_MEMBERS_MODAL_SEARCH_TOOLTIP" />
          <Input
            tiny
            width="200px"
            onChange={searchOnChange}
            addClass="ml-8"
            placeholder={t("RECIPIENT_MEMBERS_MODAL_SEARCH_INPUT_TOOLTIP")}
          />
        </div>
        <ModalRecipientMembersList
          members={filteredMembers}
          organization={organization}
          currentUserId={currentUser?.id}
          handleAddMember={(member) =>
            setMembersToReceive((prevState) => [...prevState, member])
          }
        />
        <div className="mt-4 pl-2 pr-2">
          <div className="mb-2 __bold __uppercase">
            {t("RECIPIENT_MEMBERS_MODAL_EMAIL_TITLE").format(organization.name)}
          </div>
          <div>{t("RECIPIENT_MEMBERS_MODAL_EMAIL_DESCRIPTION")}</div>
          <div className="df mt-4">
            <IconMailLetter height="30" width="30" color={color.purple} addClass="mr-4" />
            <AddableFormikField
              type="email"
              allowEmpty
              small
              initialValue=""
              loading={emailLoading}
              placeholder="INVITE_SOMEONE_BY_EMAIL_02"
              addPermissions={["ADD_RECIPIENTS_IN_RFT"]}
              add={(value) => {
                onChoiceNotificationMember(membersToReceive);
                onAddEmailRecipients(value);
                onClose();
              }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
